import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@devinforius/i-essentials';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { HttpErrorModalComponent } from 'src/app/components/modals/http-error-modal/http-error-modal.component';
import { DialogService } from 'primeng/dynamicdialog';
import { AllErrorResponse } from '../shared/models/error-response-from-backend';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  public modalReference: NgbModalRef;

  constructor(
    private dialogService: DialogService,
    public router: Router,
    public toastr: ToastrService,
    public translater: TranslateService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next
      .handle(request)

      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = '';
          const { status } = error;

          let warnings = [];
          let errorsMsg = [];

          if (status === 0 && error.error instanceof ProgressEvent && environment.production) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('Client side error:', error.error);
            this.toastr.warning('Nous avons détecté une erreur de connexion internet, vous allez être déconnecté(e)');
          }

          // #region error 400
          else if (error.status === 400) {
            // error with warning
            if (error.error.message) {
              const errorCode = error.error.code;

              switch (errorCode) {
                case 2001:
                  this.toastr.error('Une erreur est survenue lors de la création de la fiche courrier');
                  break;
                case 2056:
                  this.toastr.error("Le dossier cible n'est pas une farde");
                  break;
                case 2057:
                  this.toastr.error("Le dossier cible n'est pas une farde");
                  break;
                case 2058:
                  this.toastr.error('La chemise est déjà présente dans le dossier choisi. Veuillez en choisir un autre');
                  break;
                case 2060:
                  this.toastr.error('Il existe au moins un courrier dans une chemise');
                  break;
                case 2061:
                  this.toastr.error('Il existe au moins un courrier dans une farde');
                  break;
                case 2062:
                  this.toastr.error('Il existe au moins une chemise dans une farde');
                  break;
                case 2402:
                  this.toastr.error("L'ID du courrier à lier n'existe pas ");
                  break;
                default:
                  if (errorCode !== 6008) {
                    if (error.error.errors?.length) {
                      error.error.errors.map((err) => {
                        //Ensuite on check si le message d'erreur contient cette phrase => utilisé dans les paramètres personnels de sauvegarde de recherche
                        if (err.message.includes('PersonalParams Name already used')) {
                          this.toastr.error('La description / le nom existe déjà.');
                        } else {
                          this.toastr.error(err.message);
                        }
                      });
                    } else {
                      // Au cas ou il n'y aurait pas d'array d'erreurs, on va vérifier le code
                      // 1009 = duplication de donnée existante
                      if (error.error.code === 1009) {
                        this.toastr.error('Une donnée existe déjà avec une information identique! Veuillez vérifier vos données.');
                      } else {
                        this.toastr.error(error.error.message);
                      }
                    }
                  }
              }
            }
            if (error.error.warnings && error.error.warnings.length > 0) {
              warnings = error.error.warnings;
              this.openErrorModal(warnings, error.error.message, 'warningMode');
            }
            // validation error
            if (error.error.errors && error.error.errors.length > 0) {
              errorsMsg = error.error.errors;
              this.openErrorModal(errorsMsg, error.error.message, 'errorsMode');
            }
          } else if (error.status === 401) {
            // #region error 401
            // alert(error);
            if (error.error && error.error.code === 1005) {
              this.toastr.warning("Vous n'avez pas le droit d'être ici, vous allez être déconnecté");
              // this.oauthService.logOut();
              // sessionStorage.clear();
            } else {
              this.toastr.error("Vous devez avoir la permission d'effectuer cette action");
            }
          } else if (error.status === 403) {
            // #region error 403
            // Mail Access denied
            const errorCode = error.error.code;
            if (errorCode === 2003) {
              this.toastr.error("Vous n'avez pas le droit de voir le courrier");
            } else if (errorCode === 2020) {
              this.toastr.error('Le courrier a été supprimé');
            } else if (errorCode === 2021) {
              this.toastr.error("Le courrier n'est actuellement pas validé");
            } else if (errorCode === 2022) {
              this.toastr.error('Le courrier doit encore être complété');
            } else if (errorCode === 2023) {
              this.toastr.error("L'accès à ce courrier est confidentiel");
            } else if (error) {
              this.toastr.error("Vous devez avoir la permission d'effectuer cette action");
            }
          } else if (error.status === 404) {
            //#region error 404
            console.log(error);
            if (error.url.includes('Me/ImageSignature')) {
              return;
            }
            const errorCode = error.error.code;
            if (errorCode === 2004) {
              const mailId = error.error.instance.replace('/api/v2/Mails/', '');
              this.toastr.error(
                mailId
                  ? "Le courrier avec l'id " + mailId + " n'existe pas. Veuillez vérifier si vous avez inscrit le bon id."
                  : "Le courrier n'existe pas. Veuillez vérifier si vous avez inscrit le bon id"
              );
            } else if (error.error?.message === 'Workflow instance not found') {
              //workflow
              this.toastr.error('Le flux est introuvable.');
            } else if (error.error instanceof Blob) {
              //blob
              if (error.url.includes('Files') && error.url.includes('Download')) {
                this.toastr.error(
                  this.translater.translate('errorInterceptor.theFileMayHaveBeenRemovedRenamedOrDeleted'),
                  this.translater.translate('admin.warning')
                );
              } else if (error.url.includes('/Me/Avatar') || error.url.includes('Logo')) {
                //Pour l'instant on ne veut rien afficher pour ces requêtes, donc faut les catch afin que le toastr n'affiche rien!
              } else {
                this.toastr.error(this.translater.translate('errorInterceptor.errorOccurredWithTheFile'), this.translater.translate('admin.warning'));
              }
            } else {
              //autre
              this.translater.translate('errorOccurred');
            }
          }
          return throwError(errorMessage);
        })
      );
  }

  openErrorModal(messages: AllErrorResponse[], globalMessage: string, mode: string) {
    this.dialogService.open(HttpErrorModalComponent, {
      styleClass: 'super-compos-modal-content',
      header: this.translater.translate('error.errorsList'),
      dismissableMask: false,
      width: '70vw',
      closable: true,
      showHeader: true,
      baseZIndex: 1000,
      data: {
        messages: messages,
        mode: mode,
        globalMessage: globalMessage,
      },
    });
  }
}
